// API Service for Profits Calculator with localStorage fallback
class ApiService {
  constructor() {
    this.baseURL = '/api';
    this.isMigrated = false;
    this.migrationInProgress = false;
    this.onUnauthorized = null; // set by App.jsx to trigger login page
  }

  // ================================
  // INITIALIZATION & MIGRATION
  // ================================

  async initialize() {
    if (this.migrationInProgress) {
      return;
    }

    try {
      // Check if we need to migrate localStorage data
      const hasLocalStorageData = this.hasLocalStorageData();
      const hasDatabaseData = await this.hasDatabaseData();

      if (hasLocalStorageData && !hasDatabaseData) {
        console.log('Migrating localStorage data to database...');
        await this.migrateLocalStorageToDatabase();
      }

      this.isMigrated = true;
    } catch (error) {
      console.error('Failed to initialize API service:', error);
      console.log('Falling back to localStorage mode');
      this.isMigrated = false;
    }
  }

  hasLocalStorageData() {
    const keys = ['productCosts', 'shippingCosts', 'supplyCosts', 'commissionRates', 'fulfillmentTypes'];

    return keys.some(key => {
      const data = localStorage.getItem(key);
      return data && data !== '{}' && data !== '[]' && data !== 'null';
    });
  }

  async hasDatabaseData() {
    try {
      const response = await fetch(`${this.baseURL}/products`);
      if (!response.ok) return false;
      
      const products = await response.json();
      return products.length > 0;
    } catch {
      return false;
    }
  }

  async migrateLocalStorageToDatabase() {
    if (this.migrationInProgress) return;
    this.migrationInProgress = true;

    try {
      // Collect all localStorage data
      const localStorageData = {
        productCosts: this.getFromLocalStorage('productCosts', {}),
        shippingCosts: this.getFromLocalStorage('shippingCosts', {}),
        supplyCosts: this.getFromLocalStorage('supplyCosts', {}),
        commissionRates: this.getFromLocalStorage('commissionRates', {}),
        fulfillmentTypes: this.getFromLocalStorage('fulfillmentTypes', {})
      };

      // Send migration request to backend
      const response = await fetch(`${this.baseURL}/migrate/localStorage`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(localStorageData)
      });

      if (!response.ok) {
        throw new Error('Migration failed');
      }

      const result = await response.json();
      console.log('Migration successful:', result);

      // Create backup of localStorage data before clearing
      this.createBackup(localStorageData);

      // Clear localStorage after successful migration
      this.clearMigratedLocalStorage();

      // Set migration flag
      localStorage.setItem('migrated_to_database', 'true');

      this.isMigrated = true;
    } catch (error) {
      console.error('Migration failed:', error);
      throw error;
    } finally {
      this.migrationInProgress = false;
    }
  }

  createBackup(data) {
    const backup = {
      ...data,
      backupDate: new Date().toISOString(),
      version: '1.0'
    };

    const jsonString = JSON.stringify(backup, null, 2);
    const blob = new Blob([jsonString], { type: 'application/json' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    
    const date = new Date().toISOString().slice(0, 10);
    const time = new Date().toISOString().slice(11, 19).replace(/:/g, '-');
    link.href = url;
    link.setAttribute('download', `profits-calculator-migration-backup-${date}_${time}.json`);
    
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
    
    console.log('Backup created successfully');
  }

  clearMigratedLocalStorage() {
    const keysToClear = [
      'productCosts', 'shippingCosts', 'supplyCosts', 'commissionRates', 'fulfillmentTypes'
    ];

    keysToClear.forEach(key => {
      localStorage.removeItem(key);
    });
  }

  getFromLocalStorage(key, defaultValue) {
    try {
      const item = localStorage.getItem(key);
      return item ? JSON.parse(item) : defaultValue;
    } catch {
      return defaultValue;
    }
  }

  // ================================
  // GENERIC API METHODS
  // ================================

  async apiRequest(endpoint, options = {}) {
    const url = `${this.baseURL}${endpoint}`;
    const config = {
      headers: {
        'Content-Type': 'application/json',
        ...options.headers,
      },
      ...options,
    };

    try {
      const response = await fetch(url, config);

      if (response.status === 401) {
        if (this.onUnauthorized) this.onUnauthorized();
        throw Object.assign(new Error('Session expirée'), { status: 401 });
      }

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      return await response.json();
    } catch (error) {
      console.error('API request failed:', error);
      throw error;
    }
  }

  async get(endpoint) {
    if (!this.isMigrated) {
      throw new Error('API not available, using localStorage fallback');
    }
    return this.apiRequest(endpoint, { method: 'GET' });
  }

  async post(endpoint, data) {
    if (!this.isMigrated) {
      throw new Error('API not available, using localStorage fallback');
    }
    return this.apiRequest(endpoint, {
      method: 'POST',
      body: JSON.stringify(data),
    });
  }

  async put(endpoint, data) {
    if (!this.isMigrated) {
      throw new Error('API not available, using localStorage fallback');
    }
    return this.apiRequest(endpoint, {
      method: 'PUT',
      body: JSON.stringify(data),
    });
  }

  async delete(endpoint) {
    if (!this.isMigrated) {
      throw new Error('API not available, using localStorage fallback');
    }
    return this.apiRequest(endpoint, { method: 'DELETE' });
  }

  // ================================
  // PRODUCTS API
  // ================================

  async getProducts() {
    try {
      return await this.get('/products');
    } catch (error) {
      console.log('Using localStorage fallback for products');
      return this.getProductsFromLocalStorage();
    }
  }

  getProductsFromLocalStorage() {
    const productCosts = this.getFromLocalStorage('productCosts', {});
    const shippingCosts = this.getFromLocalStorage('shippingCosts', {});
    const supplyCosts = this.getFromLocalStorage('supplyCosts', {});
    const commissionRates = this.getFromLocalStorage('commissionRates', {});
    const fulfillmentTypes = this.getFromLocalStorage('fulfillmentTypes', {});

    const allSkus = new Set([
      ...Object.keys(productCosts),
      ...Object.keys(shippingCosts),
      ...Object.keys(supplyCosts),
      ...Object.keys(commissionRates),
      ...Object.keys(fulfillmentTypes)
    ]);

    return Array.from(allSkus).map(sku => ({
      sku,
      product_cost_ht: parseFloat(productCosts[sku] || '0'),
      shipping_cost_ht: parseFloat(shippingCosts[sku] || '0'),
      supply_cost_ht: parseFloat(supplyCosts[sku] || '0'),
      commission_rate: parseFloat(commissionRates[sku] || '15'),
      fulfillment_type: fulfillmentTypes[sku] || 'FBA'
    }));
  }

  async getProduct(sku) {
    try {
      return await this.get(`/products/${encodeURIComponent(sku)}`);
    } catch (error) {
      console.log('Using localStorage fallback for single product');
      const products = this.getProductsFromLocalStorage();
      return products.find(p => p.sku === sku) || null;
    }
  }

  async updateProductCosts(updates) {
    try {
      return await this.put('/products/costs/batch', { updates });
    } catch (error) {
      console.log('Using localStorage fallback for product cost updates');
      
      // Group updates by field
      const fieldGroups = {};
      updates.forEach(update => {
        if (!fieldGroups[update.field]) {
          fieldGroups[update.field] = {};
        }
        fieldGroups[update.field][update.sku] = update.value;
      });

      // Update localStorage
      const storageMapping = {
        'product_cost_ht': 'productCosts',
        'shipping_cost_ht': 'shippingCosts',
        'supply_cost_ht': 'supplyCosts',
        'commission_rate': 'commissionRates',
        'fulfillment_type': 'fulfillmentTypes'
      };

      Object.entries(fieldGroups).forEach(([field, skuValues]) => {
        const storageKey = storageMapping[field];
        if (storageKey) {
          const currentData = this.getFromLocalStorage(storageKey, {});
          Object.entries(skuValues).forEach(([sku, value]) => {
            currentData[sku] = value.toString();
          });
          localStorage.setItem(storageKey, JSON.stringify(currentData));
        }
      });

      return { success: true, updated: updates.length };
    }
  }

  // ================================
  // CONFIGURATION API
  // ================================

  async getConfiguration() {
    try {
      return await this.get('/config');
    } catch (error) {
      console.log('Using localStorage fallback for configuration');
      return {
        productCosts: this.getFromLocalStorage('productCosts', {}),
        shippingCosts: this.getFromLocalStorage('shippingCosts', {}),
        supplyCosts: this.getFromLocalStorage('supplyCosts', {}),
        commissionRates: this.getFromLocalStorage('commissionRates', {}),
        fulfillmentTypes: this.getFromLocalStorage('fulfillmentTypes', {})
      };
    }
  }

  async saveConfigurationSnapshot(name, configData) {
    try {
      return await this.post('/config/snapshot', {
        snapshot_name: name,
        config_data: configData
      });
    } catch (error) {
      console.log('Using localStorage fallback for configuration snapshot');
      // Save to localStorage with timestamp
      const timestamp = Date.now();
      const snapshotKey = `config_snapshot_${timestamp}`;
      const snapshot = {
        name: name || `Snapshot ${new Date().toLocaleString()}`,
        data: configData,
        created_at: new Date().toISOString(),
        id: timestamp
      };
      localStorage.setItem(snapshotKey, JSON.stringify(snapshot));
      return { success: true, snapshot_id: timestamp };
    }
  }

  async getConfigurationSnapshots() {
    try {
      return await this.get('/config/snapshots');
    } catch (error) {
      console.log('Using localStorage fallback for configuration snapshots');
      const snapshots = [];
      for (let i = 0; i < localStorage.length; i++) {
        const key = localStorage.key(i);
        if (key && key.startsWith('config_snapshot_')) {
          try {
            const snapshot = JSON.parse(localStorage.getItem(key));
            snapshots.push({
              id: snapshot.id,
              snapshot_name: snapshot.name,
              created_at: snapshot.created_at,
              version: '1.0'
            });
          } catch (e) {
            console.error('Error parsing snapshot:', e);
          }
        }
      }
      return snapshots.sort((a, b) => new Date(b.created_at) - new Date(a.created_at));
    }
  }

  async restoreConfiguration(snapshotId) {
    try {
      return await this.post(`/config/restore/${snapshotId}`);
    } catch (error) {
      console.log('Using localStorage fallback for configuration restore');
      const snapshotKey = `config_snapshot_${snapshotId}`;
      const snapshotData = localStorage.getItem(snapshotKey);
      
      if (!snapshotData) {
        throw new Error('Snapshot not found');
      }
      
      const snapshot = JSON.parse(snapshotData);
      const config = snapshot.data;
      
      // Restore to localStorage
      Object.entries(config).forEach(([key, value]) => {
        localStorage.setItem(key, JSON.stringify(value));
      });
      
      return { success: true, restored_snapshot_id: snapshotId };
    }
  }

  // ================================
  // TRANSACTIONS API
  // ================================

  async importTransactions(transactions, filename) {
    try {
      const sessionId = Date.now().toString();
      return await this.post('/transactions/import', {
        transactions,
        import_filename: filename,
        import_session_id: sessionId
      });
    } catch (error) {
      console.log('Using localStorage fallback for transaction import');
      // For localStorage fallback, we don't store transactions as they're temporary
      // Just return success for compatibility
      return { 
        success: true, 
        imported: transactions.length,
        session_id: Date.now().toString(),
        note: 'Data stored temporarily in memory (localStorage mode)'
      };
    }
  }

  async getTransactions(filters = {}) {
    try {
      const queryParams = new URLSearchParams(filters).toString();
      return await this.get(`/transactions${queryParams ? '?' + queryParams : ''}`);
    } catch (error) {
      console.log('Using localStorage fallback for transactions');
      // In localStorage mode, transactions are not persisted
      return [];
    }
  }

  // ================================
  // SETTINGS API
  // ================================

  async getSettings() {
    try {
      return await this.get('/settings');
    } catch (error) {
      console.log('Using localStorage fallback for settings');
      return {}; // No specific settings for Profits Calculator in localStorage mode
    }
  }

  async updateSetting(key, value) {
    try {
      return await this.put(`/settings/${key}`, { value });
    } catch (error) {
      console.log('Using localStorage fallback for setting update');
      localStorage.setItem(`setting_${key}`, String(value));
      return { key, value };
    }
  }

  // ================================
  // UTILITY METHODS
  // ================================

  async resetAllData() {
    try {
      return await this.post('/reset');
    } catch (error) {
      console.log('Using localStorage fallback for data reset');
      // Clear all relevant localStorage keys
      const keysToClear = [
        'productCosts', 'shippingCosts', 'supplyCosts', 'commissionRates', 'fulfillmentTypes'
      ];
      
      keysToClear.forEach(key => {
        localStorage.removeItem(key);
      });
      
      // Clear snapshots
      for (let i = localStorage.length - 1; i >= 0; i--) {
        const key = localStorage.key(i);
        if (key && key.startsWith('config_snapshot_')) {
          localStorage.removeItem(key);
        }
      }
      
      return { success: true, message: 'All data has been reset (localStorage mode)' };
    }
  }

  async exportData() {
    try {
      return await this.get('/export');
    } catch (error) {
      console.log('Using localStorage fallback for data export');
      return {
        // Export localStorage format for compatibility
        productCosts: this.getFromLocalStorage('productCosts', {}),
        shippingCosts: this.getFromLocalStorage('shippingCosts', {}),
        supplyCosts: this.getFromLocalStorage('supplyCosts', {}),
        commissionRates: this.getFromLocalStorage('commissionRates', {}),
        fulfillmentTypes: this.getFromLocalStorage('fulfillmentTypes', {}),
        exportDate: new Date().toISOString(),
        appVersion: '1.0',
        source: 'localStorage'
      };
    }
  }

  // ================================
  // DATA OPERATIONS HELPERS
  // ================================

  // Helper method to update a specific cost field for a SKU
  async updateProductField(sku, field, value) {
    const updates = [{ sku, field, value }];
    return await this.updateProductCosts(updates);
  }

  // Helper method to batch update multiple SKUs for the same field
  async batchUpdateField(field, skuValuePairs) {
    const updates = Object.entries(skuValuePairs).map(([sku, value]) => ({
      sku, field, value
    }));
    return await this.updateProductCosts(updates);
  }

  // Helper to normalize decimal values (replaces comma with dot)
  normalizeDecimalValue(value) {
    if (typeof value === 'string') {
      return value.replace(',', '.');
    }
    return value;
  }
}

// Export singleton instance
const apiService = new ApiService();
export default apiService;